import { useEffect, useRef, useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import { DEVICE_SIZES, useWindowSize } from 'lib/hooks/useWindowSize';

import Icon from 'components/shared/Icon/Icon';

import mascot from 'assets/Images/myriad-motion/mascot.png';

import { SMART_SUGGESTIONS_DEFAULT_TITLE } from '../../lib/constants';

const SmartSuggestions = ({
  smartSuggestions,
  title = SMART_SUGGESTIONS_DEFAULT_TITLE,
  compact = false
}) => {
  const { device } = useWindowSize();
  const [active, setActive] = useState(0);
  const [isExpanded, setIsExpanded] = useState(device !== DEVICE_SIZES.MOBILE);
  const touchStartX = useRef(0);

  const items = ia(smartSuggestions)
    ? smartSuggestions
    : smartSuggestions
      ? [smartSuggestions]
      : [];
  const activeItem = items[active];
  const isObjectItem = activeItem && typeof activeItem === 'object';
  const activeTitle = (isObjectItem && activeItem.title) || title;
  const activeBody = isObjectItem ? activeItem.body : activeItem;

  useEffect(() => {
    const len = items.length;
    if (len <= 1) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % len);
    }, 30000);
    return () => clearInterval(id);
  }, [items.length]);

  useEffect(() => {
    setActive((prev) => (prev >= items.length ? 0 : prev));
  }, [items.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!isExpanded) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) < threshold) return;

    const len = items.length;
    if (len === 0) return;

    if (diff > 0) {
      // Swiped left - go to next
      setActive((prev) => (prev + 1) % len);
    } else {
      // Swiped right - go to previous
      setActive((prev) => (prev === 0 ? len - 1 : prev - 1));
    }
  };

  return (
    <div
      className={cn(
        'sidebar-messages flex flex-col gap-1 overflow-hidden rounded-xl bg-white px-4 py-2 transition-all duration-300 ease-in-out',
        compact
          ? 'justify-between'
          : isExpanded
            ? 'h-[252px] max-h-[252px] justify-between'
            : 'max-h-[52px] justify-start',
        device === DEVICE_SIZES.MOBILE &&
          'rounded-none border-b border-l-0 border-r-0 border-t border-solid border-neutral-300'
      )}
      onTouchStart={device === DEVICE_SIZES.MOBILE ? handleTouchStart : undefined}
      onTouchEnd={device === DEVICE_SIZES.MOBILE ? handleTouchEnd : undefined}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <img src={mascot} alt="Myriad mascot" className="h-10 w-10 object-contain" />
            <p className="text-xs font-600">{activeTitle}</p>
          </div>
          <div className="flex gap-2">
            {device === DEVICE_SIZES.MOBILE ? (
              <div className="rounded-md border border-solid border-neutral-200">
                <Icon
                  className="cursor-pointer select-none"
                  icon={isExpanded ? 'minimize-linear' : 'maximize-linear'}
                  onClick={() => setIsExpanded(!isExpanded)}
                />
              </div>
            ) : (
              items.length > 1 && (
                <>
                  <div className="rounded-md border border-solid border-neutral-200">
                    <Icon
                      className="cursor-pointer select-none"
                      icon="chevron-left-linear"
                      onClick={() => {
                        const len = items.length;
                        if (len === 0) return;
                        setActive((prev) => (prev === 0 ? len - 1 : prev - 1));
                      }}
                    />
                  </div>
                  <div className="rounded-md border border-solid border-neutral-200">
                    <Icon
                      className="cursor-pointer select-none"
                      icon="chevron-right-linear"
                      onClick={() => {
                        const len = items.length;
                        if (len === 0) return;
                        setActive((prev) => (prev + 1) % len);
                      }}
                    />
                  </div>
                </>
              )
            )}
          </div>
        </div>
        <div className={cn('mb-1 text-sm', !isExpanded && 'hidden h-0')}>{activeBody}</div>
      </div>
      {items.length > 1 && (
        <div className={cn('flex w-full items-center justify-center', !isExpanded && 'hidden')}>
          <Icon icon={`carousel-dot-${(active % 5) + 1}`} />
        </div>
      )}
    </div>
  );
};

export default SmartSuggestions;
